// Title requests (F-CAT-08, spec/14 §6): titles venues found installed on their PCs that the catalogue does not hold,
// asked for from the venue console's "Detected, not in library". One request per title, however many venues asked;
// open first, then the most recently asked. HQ finds the title on IGDB (the Games view's search, prefilled) and marks
// the request added with the catalogue game that answers it — or dismisses it.

import type { CatalogueRequest, Game } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, EmptyState, SelectField, useToast } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { LoadFailure } from '../../shell/LoadFailure';
import { Icon } from '../../shell/nav-icons';
import { CardsSkeleton } from '../../ui';
import { useCatalogueGames, useCatalogueRequests, useResolveCatalogueRequest } from './queries';
import { useProblem } from '../../api/use-problem';

/** The picker's "no game chosen" value (a select item may not be empty). */
const NONE = 'none';

/** The launchers' own names (data, not copy). */
const SOURCE_NAMES: Readonly<Record<CatalogueRequest['source'], string>> = {
  steam: 'Steam',
  epic: 'Epic Games',
  ubisoft: 'Ubisoft Connect',
  ea: 'EA app',
  battlenet: 'Battle.net',
};

export function RequestsInbox({ onFind }: { readonly onFind: (name: string) => void }): ReactNode {
  const { t } = useTranslation('hq');
  const requests = useCatalogueRequests('all');
  const games = useCatalogueGames('game');
  const catalogue = games.data?.pages.flatMap((page) => page.data) ?? [];

  let body: ReactNode;
  if (requests.data === undefined) {
    body = requests.isError ? (
      <LoadFailure error={requests.error} message={t('catalogue.requests.failed')} onRetry={() => void requests.refetch()} />
    ) : (
      <CardsSkeleton label={t('catalogue.requests.caption')} />
    );
  } else if (requests.data.data.length === 0) {
    body = <EmptyState icon={<Icon name="layers" />} title={t('catalogue.requests.empty')} />;
  } else {
    body = (
      <ul className="console-catalogue-requests" aria-label={t('catalogue.requests.caption')}>
        {requests.data.data.map((request) => (
          <RequestRow key={request.id} request={request} catalogue={catalogue} onFind={onFind} />
        ))}
      </ul>
    );
  }

  return (
    <section className="console-catalogue-section" aria-labelledby="console-catalogue-requests-title">
      <div className="console-catalogue-bar">
        <div className="console-catalogue-bar__text">
          <h2 id="console-catalogue-requests-title">{t('catalogue.requests.title')}</h2>
          <p className="console-muted">{t('catalogue.requests.intro')}</p>
        </div>
      </div>
      {body}
    </section>
  );
}

function RequestRow({ request, catalogue, onFind }: { readonly request: CatalogueRequest; readonly catalogue: readonly Game[]; readonly onFind: (name: string) => void }): ReactNode {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const resolve = useResolveCatalogueRequest();
  // The catalogue game whose name matches is the likely answer; HQ can pick another.
  const guess = catalogue.find((game) => game.name.trim().toLowerCase() === request.name.trim().toLowerCase());
  const [gameId, setGameId] = useState<string>(guess?.id ?? NONE);
  const open = request.status === 'open';
  const answered = catalogue.find((game) => game.id === request.gameId);

  const answer = (status: 'added' | 'dismissed') =>
    resolve.mutate(
      { requestId: request.id, body: status === 'added' ? { status, gameId: gameId as Game['id'] } : { status } },
      {
        onSuccess: () => show({ tone: 'ok', title: status === 'added' ? t('catalogue.requests.added', { name: request.name }) : t('catalogue.requests.dismissed', { name: request.name }) }),
        onError: (err) => show({ tone: 'danger', title: problems.text(err, { fallback: t('catalogue.requests.resolveFailed') }) }),
      },
    );

  return (
    <li className="console-catalogue-request">
      <div className="console-catalogue-request__head">
        <strong>{request.name}</strong>
        <Badge tone={open ? 'warn' : request.status === 'added' ? 'ok' : 'neutral'}>{t(`catalogue.requests.status.${request.status}`)}</Badge>
      </div>
      <p className="console-muted">
        {SOURCE_NAMES[request.source]} · {request.sourceId} · {t('catalogue.requests.count', { count: request.requestCount })} ·{' '}
        {t('catalogue.requests.lastAsked', { date: new Date(request.lastRequestedAt).toLocaleDateString() })}
      </p>
      {request.status === 'added' && answered !== undefined ? <p className="console-muted">{t('catalogue.requests.answeredBy', { game: answered.name })}</p> : null}
      {open ? (
        <div className="console-catalogue-request__actions">
          <Button variant="secondary" size="sm" icon={<Icon name="search" />} label={t('catalogue.requests.findLabel', { name: request.name })} onClick={() => onFind(request.name)}>
            <span>{t('catalogue.requests.find')}</span>
          </Button>
          <SelectField
            label={t('catalogue.requests.pickGame')}
            value={gameId}
            onValueChange={setGameId}
            options={[{ value: NONE, label: t('catalogue.requests.pickNone') }, ...catalogue.map((game) => ({ value: game.id, label: game.name }))]}
          />
          <Button variant="primary" size="sm" busy={resolve.isPending} disabled={gameId === NONE} onClick={() => answer('added')}>
            {t('catalogue.requests.markAdded')}
          </Button>
          <Button variant="ghost" size="sm" busy={resolve.isPending} onClick={() => answer('dismissed')}>
            {t('catalogue.requests.dismiss')}
          </Button>
        </div>
      ) : null}
    </li>
  );
}
